import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { EditableCell, StatusPill } from '../components/InlineEditTable';
import { useBulkSelect } from '../hooks/useBulkSelect';
import SearchableSelect from '../components/SearchableSelect';
import DataTable from '../components/DataTable';
import { useCurrentUser } from '../context/UserContext';
import FormField from '../components/FormField';
import { useFormValidation } from '../hooks/useFormValidation';
import { buildStatusGroupMap, buildProgramOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  'Forecast': '#f39c12',
  'Invoiced': '#3498db',
  'Paid': '#27ae60',
};

const emptyForm = () => ({
  invoice_number: '', program_id: '', invoice_date: '', invoice_amount: '', status: '', notes: '',
});

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };

const INVOICE_RULES = {
  program_id: { required: true, label: 'Program' },
  invoice_date: { required: true, label: 'Invoice Date' },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on every Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programOptionGroups = buildProgramOptionGroups(programs, buildStatusGroupMap(programStatuses));
  const [statuses, setStatuses] = useState([]);
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const location = useLocation();
  const pendingEditId = useRef(location.state?.editInvoiceId || null);
  const { currentUser } = useCurrentUser();
  // Invoices are financial records — only financial/admin may create/edit/delete.
  const canWrite = ['admin', 'financial'].includes(currentUser?.role);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const { selected, toggle, toggleAll, clear } = useBulkSelect();
  const { errors, validate, reset: resetErrors } = useFormValidation(INVOICE_RULES);

  const fetchInvoices = () => axios.get(`${API}/invoices/`).then(r => setInvoices(r.data)).catch(() => {});

  // Persist a single inline-edited field. update_invoice returns the refreshed row
  // directly (flat schema, no nested relations) — use it instead of a full refetch.
  const saveField = async (id, patch) => {
    const res = await axios.put(`${API}/invoices/${id}`, patch);
    setInvoices(prev => prev.map(inv => inv.id === id ? res.data : inv));
  };

  const bulkSetStatus = async (status) => {
    await Promise.all([...selected].map(id => axios.put(`${API}/invoices/${id}`, { status })));
    clear();
    await fetchInvoices();
  };

  const handleBackfillCloseInvoiced = async () => {
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/invoices/admin/backfill-close-invoiced`);
      setBackfillResult(res.data);
    } catch (err) {
      alert('Backfill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillBusy(false);
    }
  };

  useEffect(() => {
    if (pendingEditId.current && invoices.length > 0) {
      const inv = invoices.find(i => i.id === pendingEditId.current);
      if (inv) { handleEdit(inv); pendingEditId.current = null; }
    }
  }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchInvoices();
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=invoice_status`).then(r => setStatuses(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    const data = {
      ...form,
      program_id: form.program_id ? parseInt(form.program_id) : null,
      invoice_amount: form.invoice_amount !== '' ? parseFloat(form.invoice_amount) : null,
    };
    if (editing) {
      await axios.put(`${API}/invoices/${editing}`, data);
      setEditing(null);
    } else {
      await axios.post(`${API}/invoices/`, data);
    }
    setForm(emptyForm());
    setShowForm(false);
    resetErrors();
    fetchInvoices();
  };

  const handleEdit = (inv) => {
    setEditing(inv.id);
    setForm({
      invoice_number: inv.invoice_number || '',
      program_id: inv.program_id || '',
      invoice_date: inv.invoice_date || '',
      invoice_amount: inv.invoice_amount !== null && inv.invoice_amount !== undefined ? inv.invoice_amount : '',
      status: inv.status || '',
      notes: inv.notes || '',
    });
    setShowForm(true);
    resetErrors();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this invoice?')) {
      await axios.delete(`${API}/invoices/${id}`);
      fetchInvoices();
    }
  };

  const programName = (id) => programs.find(p => p.id === id)?.program_name || '—';

  const filtered = invoices.filter(i =>
    (!filterProgram || String(i.program_id) === filterProgram) &&
    (!filterStatus || i.status === filterStatus)
  );
  // Inline-edit option lists. Fall back to the legacy hardcoded statuses if the
  // lookup category is empty.
  const programOpts = programs.map(p => ({ value: p.id, label: p.program_name }));
  const statusList = statuses.length ? statuses : ['Forecast', 'Invoiced', 'Paid'].map((v, i) => ({ id: `fallback-${i}`, value: v }));
  const statusOpts = [{ value: '', label: '—' }, ...statusList.map(s => ({ value: s.value, label: s.value }))];

  const fmtAmount = (a) => a != null ? '$' + Number(a).toLocaleString('en-US', { minimumFractionDigits: 2 }) : undefined;

  const columns = [
    { key: 'invoice_number', header: 'Invoice #', width: 130, render: inv => canWrite
        ? <EditableCell value={inv.invoice_number} onSave={v => saveField(inv.id, { invoice_number: v })} />
        : (inv.invoice_number || '—') },
    { key: 'program', header: 'Program', width: 180, render: inv => canWrite
        ? <EditableCell type="select" value={inv.program_id} options={programOpts} display={programName(inv.program_id)}
            onSave={v => saveField(inv.id, { program_id: v ? parseInt(v) : null })} />
        : programName(inv.program_id) },
    { key: 'invoice_date', header: 'Invoice Date', width: 150, render: inv => canWrite
        ? <EditableCell type="date" value={inv.invoice_date} onSave={v => saveField(inv.id, { invoice_date: v || null })} />
        : (inv.invoice_date || '—') },
    { key: 'invoice_amount', header: 'Amount', width: 120, render: inv => canWrite
        ? <EditableCell type="number" step="0.01" value={inv.invoice_amount} display={fmtAmount(inv.invoice_amount)}
            onSave={v => saveField(inv.id, { invoice_amount: v === '' ? null : parseFloat(v) })} />
        : (fmtAmount(inv.invoice_amount) || '—') },
    { key: 'status', header: 'Status', width: 110, render: inv => canWrite
        ? <EditableCell type="select" value={inv.status} options={statusOpts}
            display={<StatusPill value={inv.status} lookups={statuses} fallback={STATUS_COLORS} />}
            onSave={v => saveField(inv.id, { status: v })} />
        : <StatusPill value={inv.status} lookups={statuses} fallback={STATUS_COLORS} /> },
    { key: 'actions', header: 'Actions', width: 140, nowrap: true, render: inv => canWrite ? (
        <>
          <button onClick={() => handleEdit(inv)} style={{ marginRight: '4px', padding: '4px 8px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
          <button onClick={() => handleDelete(inv.id)} style={{ padding: '4px 8px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Del</button>
        </>
      ) : '—' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Invoices</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SearchableSelect value={filterProgram} onChange={setFilterProgram}
            placeholder="All Programs"
            options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions} />
          <SearchableSelect value={filterStatus} onChange={setFilterStatus}
            placeholder="All Statuses"
            options={statusList.map(s => ({ value: s.value, label: s.value }))} />
          {currentUser?.role === 'admin' && (
            <button onClick={handleBackfillCloseInvoiced} disabled={backfillBusy}
              title='Repair programs stuck at "...Not Invoiced" despite every invoice on them already being "Invoiced" — see CLAUDE.md'
              style={{ padding: '8px 12px', background: backfillBusy ? '#ccc' : '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: backfillBusy ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
              {backfillBusy ? '🔧 Running…' : '🔧 Fix Stuck "Not Invoiced" Programs'}
            </button>
          )}
          {canWrite && <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm()); resetErrors(); }} style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ New Invoice</button>}
        </div>
      </div>

      {backfillResult && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#eafaf1', border: '1px solid #27ae60', borderRadius: '6px', fontSize: '12px', color: '#1e8449' }}>
          ✓ Checked {backfillResult.checked} program{backfillResult.checked === 1 ? '' : 's'} — fixed {backfillResult.fixed.length}.
          {backfillResult.fixed.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
              {backfillResult.fixed.map(f => (
                <li key={f.program_id}>{f.program_name}: "{f.from}" → "{f.to}"</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showForm && canWrite && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit Invoice' : 'New Invoice'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <FormField label="Invoice Number">
              <input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Program" required error={errors.program_id}>
              <SearchableSelect value={form.program_id} onChange={v => setForm({ ...form, program_id: v })}
                options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
                placeholder="Select program…" />
            </FormField>
            <FormField label="Invoice Date" required error={errors.invoice_date}>
              <input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Amount">
              <input type="number" step="0.01" value={form.invoice_amount} onChange={e => setForm({ ...form, invoice_amount: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                <option value="">Select…</option>
                {statuses.length > 0
                  ? statuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)
                  : ['Forecast', 'Invoiced', 'Paid'].map(s => <option key={s} value={s}>{s}</option>)
                }
              </select>
            </FormField>
            <FormField label="Notes">
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{editing ? 'Update' : 'Create'}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); resetErrors(); }} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </form>
      )}

      <DataTable
        rows={filtered}
        columns={columns}
        bulk={canWrite ? { selected, toggle, toggleAll, statuses: statusList, onApply: bulkSetStatus } : undefined}
        rowStyle={inv => ({ background: selected.has(inv.id) ? '#f0f7ff' : 'white' })}
        emptyText="No invoices found."
      />
    </div>
  );
}
